export const FLOWER_IDS = [
  'daisy',
  'tulip',
  'sunflower',
  'rose',
  'lavender',
  'lily',
  'orchid',
  'lotus',
  'rainbowTulip',
  'sunsetRose',
  'moonbell',
  'starbloom',
  'prismLotus',
] as const;

export type FlowerId = (typeof FLOWER_IDS)[number];

export type Rarity = 'common' | 'uncommon' | 'rare' | 'epic' | 'legendary';

/** Procedural model family used by the renderer. */
export type FlowerShape =
  | 'daisy'
  | 'tulip'
  | 'sunflower'
  | 'rose'
  | 'lavender'
  | 'lily'
  | 'orchid'
  | 'lotus'
  | 'bell'
  | 'star';

export interface FlowerDef {
  readonly id: FlowerId;
  readonly name: string;
  readonly emoji: string;
  readonly rarity: Rarity;
  /** Price of one seed at the market, or `null` for hybrids that can only be bred. */
  readonly seedCost: number | null;
  readonly sellPrice: number;
  /** Seconds of *watered* time needed to reach full bloom. */
  readonly growTime: number;
  readonly xp: number;
  readonly unlockLevel: number;
  readonly shape: FlowerShape;
  /** Petal colours; multiple entries are blended or cycled by the model builder. */
  readonly colors: readonly string[];
  readonly center: string;
  readonly glow?: 'night' | 'always';
  readonly description: string;
  /** For hybrids: the two flowers that cross-pollinate into this one. */
  readonly parents?: readonly [FlowerId, FlowerId];
}

export const FLOWERS: Readonly<Record<FlowerId, FlowerDef>> = {
  daisy: {
    id: 'daisy',
    name: 'Daisy',
    emoji: '🌼',
    rarity: 'common',
    seedCost: 4,
    sellPrice: 10,
    growTime: 25,
    xp: 2,
    unlockLevel: 1,
    shape: 'daisy',
    colors: ['#ffffff'],
    center: '#ffd23f',
    description: 'A cheerful little bloom. Every homestead starts with a daisy.',
  },
  tulip: {
    id: 'tulip',
    name: 'Tulip',
    emoji: '🌷',
    rarity: 'common',
    seedCost: 8,
    sellPrice: 20,
    growTime: 40,
    xp: 4,
    unlockLevel: 1,
    shape: 'tulip',
    colors: ['#ff7aa8', '#ff9fc2'],
    center: '#ffe066',
    description: 'Soft pink cups that bob in the prairie breeze.',
  },
  sunflower: {
    id: 'sunflower',
    name: 'Sunflower',
    emoji: '🌻',
    rarity: 'uncommon',
    seedCost: 18,
    sellPrice: 45,
    growTime: 60,
    xp: 7,
    unlockLevel: 2,
    shape: 'sunflower',
    colors: ['#ffc928', '#ffb000'],
    center: '#7a4a24',
    description: 'Tall and sunny. It always knows where the sun is.',
  },
  rose: {
    id: 'rose',
    name: 'Rose',
    emoji: '🌹',
    rarity: 'uncommon',
    seedCost: 30,
    sellPrice: 75,
    growTime: 90,
    xp: 10,
    unlockLevel: 3,
    shape: 'rose',
    colors: ['#e8364f', '#ff5c74'],
    center: '#b81f3a',
    description: 'A classic romantic. Customers adore them.',
  },
  lavender: {
    id: 'lavender',
    name: 'Lavender',
    emoji: '💜',
    rarity: 'rare',
    seedCost: 55,
    sellPrice: 135,
    growTime: 120,
    xp: 15,
    unlockLevel: 4,
    shape: 'lavender',
    colors: ['#9d7bea', '#b89cff'],
    center: '#7a5bd0',
    description: 'Fragrant purple spires. The bees go wild for it.',
  },
  lily: {
    id: 'lily',
    name: 'Lily',
    emoji: '🤍',
    rarity: 'rare',
    seedCost: 90,
    sellPrice: 220,
    growTime: 150,
    xp: 22,
    unlockLevel: 6,
    shape: 'lily',
    colors: ['#fff4f8', '#ffc4dc'],
    center: '#ff9f43',
    description: 'Elegant star-shaped petals with a blush of pink.',
  },
  orchid: {
    id: 'orchid',
    name: 'Orchid',
    emoji: '🪻',
    rarity: 'epic',
    seedCost: 160,
    sellPrice: 400,
    growTime: 200,
    xp: 32,
    unlockLevel: 8,
    shape: 'orchid',
    colors: ['#d65bd8', '#f3a6f0'],
    center: '#ffe14d',
    description: 'Exotic and fussy, but worth every drop of water.',
  },
  lotus: {
    id: 'lotus',
    name: 'Lotus',
    emoji: '🪷',
    rarity: 'epic',
    seedCost: 280,
    sellPrice: 700,
    growTime: 260,
    xp: 45,
    unlockLevel: 10,
    shape: 'lotus',
    colors: ['#ffffff', '#ff8fc0'],
    center: '#ffd23f',
    description: 'Serene layered petals that seem to float above the soil.',
  },
  rainbowTulip: {
    id: 'rainbowTulip',
    name: 'Rainbow Tulip',
    emoji: '🌈',
    rarity: 'rare',
    seedCost: null,
    sellPrice: 160,
    growTime: 70,
    xp: 18,
    unlockLevel: 1,
    shape: 'tulip',
    colors: ['#ff6b6b', '#ffb347', '#ffe66d', '#6bd66b', '#5fb4ff', '#b28dff'],
    center: '#ffffff',
    description: 'Every petal a different colour. Born where tulips and daisies mingle.',
    parents: ['tulip', 'daisy'],
  },
  sunsetRose: {
    id: 'sunsetRose',
    name: 'Sunset Rose',
    emoji: '🌅',
    rarity: 'epic',
    seedCost: null,
    sellPrice: 380,
    growTime: 120,
    xp: 30,
    unlockLevel: 1,
    shape: 'rose',
    colors: ['#ff9248', '#ff5e8a'],
    center: '#ff4f6d',
    description: 'Painted with the colours of a prairie evening.',
    parents: ['sunflower', 'rose'],
  },
  moonbell: {
    id: 'moonbell',
    name: 'Moonbell',
    emoji: '🔔',
    rarity: 'epic',
    seedCost: null,
    sellPrice: 650,
    growTime: 170,
    xp: 45,
    unlockLevel: 1,
    shape: 'bell',
    colors: ['#bfe3ff', '#8fb8ff'],
    center: '#eaf6ff',
    glow: 'night',
    description: 'Pale bells that softly glow when the moon is up.',
    parents: ['lavender', 'lily'],
  },
  starbloom: {
    id: 'starbloom',
    name: 'Starbloom',
    emoji: '🌟',
    rarity: 'legendary',
    seedCost: null,
    sellPrice: 1600,
    growTime: 240,
    xp: 80,
    unlockLevel: 1,
    shape: 'star',
    colors: ['#fff3a6', '#ffd23f'],
    center: '#ffffff',
    glow: 'always',
    description: 'Said to sprout from fallen stars. It twinkles day and night.',
    parents: ['orchid', 'lotus'],
  },
  prismLotus: {
    id: 'prismLotus',
    name: 'Prism Lotus',
    emoji: '💎',
    rarity: 'legendary',
    seedCost: null,
    sellPrice: 4000,
    growTime: 300,
    xp: 150,
    unlockLevel: 1,
    shape: 'lotus',
    colors: ['#9fe8ff', '#d2a8ff', '#ffb3d9'],
    center: '#ffffff',
    glow: 'always',
    description: 'The rarest bloom on the plains, shimmering with every colour of light.',
    parents: ['starbloom', 'rainbowTulip'],
  },
};

export const RARITY_INFO: Readonly<Record<Rarity, { label: string; color: string }>> = {
  common: { label: 'Common', color: '#8fbf6a' },
  uncommon: { label: 'Uncommon', color: '#5fb4ff' },
  rare: { label: 'Rare', color: '#b28dff' },
  epic: { label: 'Epic', color: '#ff7ac6' },
  legendary: { label: 'Legendary', color: '#ffb400' },
};

export const BUYABLE_FLOWERS: readonly FlowerId[] = FLOWER_IDS.filter(
  (id) => FLOWERS[id].seedCost !== null,
);

export const HYBRID_FLOWERS: readonly FlowerId[] = FLOWER_IDS.filter(
  (id) => FLOWERS[id].parents !== undefined,
);

export function isFlowerId(value: unknown): value is FlowerId {
  return typeof value === 'string' && (FLOWER_IDS as readonly string[]).includes(value);
}

export function isHybrid(id: FlowerId): boolean {
  return FLOWERS[id].parents !== undefined;
}

/** The hybrid produced when `a` and `b` bloom side by side, if any (order-independent). */
export function findHybrid(a: FlowerId, b: FlowerId): FlowerId | null {
  for (const id of HYBRID_FLOWERS) {
    const parents = FLOWERS[id].parents;
    if (!parents) continue;
    if ((parents[0] === a && parents[1] === b) || (parents[0] === b && parents[1] === a)) {
      return id;
    }
  }
  return null;
}
